import {
  browserTimeZone,
  DEMO_INVITATION_PATH,
  demoInvitationKeys,
} from '@/hooks/queries/use-demo-invitation-query';
import api from '@/libs/axiosInstance';
import { trackProductAction } from '@/libs/product-analytics';
import type {
  ApiResponse,
  BookDemoPayload,
  DemoBookingDto,
  DemoInvitationDto,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

/** Books a slot; the invitation read back carries the confirmation. */
export const useBookDemoAction = (brandId: string) => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  return useMutation<DemoBookingDto, unknown, string>({
    mutationFn: (start) =>
      trackProductAction('demo', 'request', async () => {
        const payload: BookDemoPayload = {
          start,
          timeZone: browserTimeZone(),
          language: i18n.language?.startsWith('fr') ? 'fr' : 'en',
        };
        const { data } = await api.post<ApiResponse<DemoBookingDto>>(
          `${DEMO_INVITATION_PATH}/booking`,
          payload
        );
        if (!data.data) throw new Error(data.message || 'Failed to book the demo');
        return data.data;
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: demoInvitationKeys.brand(brandId) }),
    onError: () =>
      // A taken slot must leave the list.
      queryClient.invalidateQueries({ queryKey: demoInvitationKeys.availabilityAll }),
  });
};

/**
 * Closes the invitation for the whole brand, or the confirmation of a booking.
 * Only closing an offer counts as declining it.
 */
export const useCloseDemoInvitationAction = (brandId: string) => {
  const queryClient = useQueryClient();
  const key = demoInvitationKeys.brand(brandId);

  return useMutation<void, unknown, { booked: boolean }>({
    mutationFn: async ({ booked }) => {
      const dismiss = () => api.post(`${DEMO_INVITATION_PATH}/dismiss`);
      await (booked ? dismiss() : trackProductAction('demo', 'decline', dismiss));
    },
    onMutate: () => {
      queryClient.setQueryData<DemoInvitationDto>(key, (current) =>
        current ? { ...current, placement: null } : current
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
};
